import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	uploadDocumentFile,
	deleteDocumentFile,
	updateDocumentProperty,
	createOnceDocument,
	requestDocumentReview,
} from "../services/suppliersProfile.service"
import Swal from "sweetalert2"

export const useDocumentComplianceMutations = ({ supplierId }) => {
	const queryClient = useQueryClient()

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ["documentCompliance", supplierId] })
		queryClient.invalidateQueries({
			predicate: query =>
				typeof query.queryKey[0] === "string" &&
				query.queryKey[0].startsWith("supplier-") &&
				query.queryKey[1]?.toString() === supplierId?.toString()
		})
	}

	const showLoading = () => {
		Swal.fire({
			toast: true,
			footer: "Loading, please wait",
			showConfirmButton: false,
			position: "bottom-end",
			didOpen: () => {
				Swal.showLoading()
			},
		})
	}

	const showError = (message) => {
		Swal.fire({
			toast: true,
			icon: "error",
			text: message || "An error occurred",
			showConfirmButton: false,
			timer: 3000,
			timerProgressBar: true,
			position: "bottom-end",
		})
	}

	const uploadMutation = useMutation({
		mutationFn: uploadDocumentFile,
		onMutate: showLoading,
		onSuccess: (data) => {
			Swal.close()
			invalidate()
			return data
		},
		onError: (err) => {
			showError(err?.response?.data?.message || "Failed to upload document")
		},
	})

	const deleteMutation = useMutation({
		mutationFn: deleteDocumentFile,
		onMutate: showLoading,
		onSuccess: () => {
			Swal.close()
			invalidate()
		},
		onError: (err) => {
			showError(err?.response?.data?.message || "Failed to delete document")
		},
	})

	const updatePropertyMutation = useMutation({
		mutationFn: updateDocumentProperty,
		onMutate: showLoading,
		onSuccess: () => {
			Swal.close()
			invalidate()
		},
		onError: (err) => {
			showError(err?.response?.data?.message || "Failed to update document")
		},
	})

	const createOnceMutation = useMutation({
		mutationFn: createOnceDocument,
		onMutate: showLoading,
		onSuccess: () => {
			Swal.close()
			invalidate()
		},
		onError: (err) => {
			showError(err?.response?.data?.message || "Failed to save document")
		},
	})

	const reviewMutation = useMutation({
		mutationFn: requestDocumentReview,
		onMutate: showLoading,
		onSuccess: () => {
			Swal.close()
			invalidate()
		},
		onError: (err) => {
			showError(err?.response?.data?.message || "Failed to request review")
		},
	})

	const handleUploadDocument = (params) => uploadMutation.mutateAsync(params)
	const handleDeleteDocument = (params) => deleteMutation.mutateAsync(params)
	const handleUpdateProperty = (params) => updatePropertyMutation.mutateAsync(params)
	const handleCreateOnce = (params) => createOnceMutation.mutateAsync(params)
	const handleRequestReview = (params) => reviewMutation.mutateAsync(params)

	return {
		handleUploadDocument,
		handleDeleteDocument,
		handleUpdateProperty,
		handleCreateOnce,
		handleRequestReview,
	}
}
